import {
	ArrowRight,
	CopyIcon,
	Crop,
	Download,
	ImagePlus,
	ImageUpscale,
	ListCollapse,
	Maximize2,
	RefreshCcw,
	Scaling,
	Shuffle,
	SplinePointer
} from "lucide-solid";
import RecolorIcon from "~/Icons/12_Icon_Magic.svg?component-solid";
import RecolorIcon16 from "~/Icons/12_Icon_Magic.svg?component-solid";
import RenderIcon16 from "~/Icons/16_Icon_3D.svg?component-solid";
import VisionIcon16 from "~/Icons/16_Icon_Brain.svg?component-solid";
import ChangeBackgroundIcon16 from "~/Icons/16_Icon_ChangeBackground.svg?component-solid";
// 16px versions for IconContext
import EditAreaIcon16 from "~/Icons/16_Icon_EditArea.svg?component-solid";
import ZoomOutIcon16 from "~/Icons/16_Icon_ZoomOut.svg?component-solid";
import type textViewer from "~/js/asset-viewport/viewers/text-viewer";
import Editor from "~/js/editor";
import { getDefinitionOrAwait } from "~/js/graph/node-library";
import { NodeEvents } from "~/js/types/IEventorListners";
import {
	fullscreenLinkedList,
	setFullScreenValues,
	setIsFullScreenModalShown
} from "~/solidJs/entities/FullScreenViewerEntity/models/fullScreenModel";
import { getUserStore } from "~/solidJs/entities/HeaderMain/models";
import { setCanvasStore } from "~/solidJs/entities/MagicBrushCanvas";
import type { IFileType } from "~/solidJs/shared";
import {
	downloadAsset,
	getIsSaveFunctionalityAvailable,
	onNextFrame,
	pickRandomFromArray
} from "~/solidJs/shared/helpers";
import {
	type AddRemoteNodeParamsRecord,
	addLocalNodeByGlobalId,
	addRemoteNodeByGlobalId,
	getPositionToTheRightWithPadding,
	isFetchesExternalModels
} from "~/solidJs/shared/helpers/nodeHelpers";
import { SidebarModes, toggleSidebarWidget } from "~/solidJs/shared/helpers/toggleSidebarWidget";
import type { IParamOptionalInfoWithValue } from "~/solidJs/shared/helpers/types/INodeDefinition";
import Icons from "~/solidJs/shared/ui/atoms/IconsComp";
import { Menu } from "~/solidJs/shared/ui/molecules/Menu";
import * as HoverFeatureMenuMethods from "./methods";
import { type HoverFeatureAction, type HoverFeatureMenuContext, ViewerType } from "./model";

// Helper to get task id from node reliably
const getTaskIdFromNode = (node: any): number | undefined => {
	return node?.taskID ?? node?.meta?.taskID ?? node?.userTemplateParams?.taskID;
};

const hoverFeatureHorizontralPadding = 100;
// Image viewer handlers
export const handleFullScreen = async (context: HoverFeatureMenuContext) => {
	if (!context.viewer || !context.socketsOutput) return;
	const asset: IFileType | undefined = await context.asset?.();
	if (!asset) return;
	// Initialize full screen if needed
	if (!(context.viewer as any).fullScreenInited) {
		(context.viewer as any).fullScreenInited = true;
		(context.viewer as any).fullScreenNode = fullscreenLinkedList.createUnShiftNode({
			nodeUuid: context.socketsOutput.node.uuid,
			asset
		});
	}

	// Update full screen asset
	(context.viewer as any).fullScreenNode.value.asset = asset;

	setFullScreenValues({
		activeItem: (context.viewer as any).fullScreenNode
	});
	setIsFullScreenModalShown(true);
};

export const handleSave = async (context: HoverFeatureMenuContext) => {
	if (context.viewerType === ViewerType.Text) {
		await handleCopy(context);
		return;
	}
	if (!context.asset) return;
	const asset = await context.asset();
	downloadAsset(asset);
};

export const handleCopy = async (context: HoverFeatureMenuContext) => {
	const viewer = context.viewer as textViewer;
	const value = await viewer.getInputValue();
	if (!value) return;
	navigator.clipboard.writeText(value);
};

export const handleInpaint = async (context: HoverFeatureMenuContext) => {
	if (!context.viewer || !context.socketsOutput) return;
	if (!("getAssetUrl" in context.viewer) || typeof context.viewer.getAssetUrl !== "function")
		return;
	const asset = await context.viewer.getAsset();

	const url = await context.viewer.getAssetUrl();

	setCanvasStore({
		url: url,
		name: asset.name.split(".")[0],
		fromNodeId: context.socketsOutput.node.uuid,
		fromNodeSocketName: context.socketName,
		mode: "inpainting"
	});
	toggleSidebarWidget(SidebarModes.Inpaint);
};

export const handleCrop = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput) return;

	await addRemoteNodeByGlobalId({
		globalId: "ImageCropper",
		connections: [
			{
				fromNodeId: context.socketsOutput.node.uuid,
				fromNodeSocketName: context.socketName,
				toNodeSocketName: "Image"
			}
		],
		position: getPositionToTheRightWithPadding(context.socketsOutput.node)
	});
};

export const handleVectorize = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput) return;

	const node = await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-api-recraft-vectorize",
		connections: [
			{
				fromNodeId: context.socketsOutput.node.uuid,
				fromNodeSocketName: context.socketName,
				toNodeSocketName: "init_img"
			}
		],
		position: getPositionToTheRightWithPadding(context.socketsOutput.node)
	});
	node?.execute();
};

export const handleUpscale = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput) return;

	const node = await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-upscale",
		connections: [
			{
				fromNodeId: context.socketsOutput.node.uuid,
				fromNodeSocketName: context.socketName,
				toNodeSocketName: "img_input"
			}
		],
		position: getPositionToTheRightWithPadding(context.socketsOutput.node)
	});
	node?.execute();
};

function buildParamsFromDefinition(definition: {
	params?: Array<{ name: string; optionalInfo?: IParamOptionalInfoWithValue }>;
}): AddRemoteNodeParamsRecord {
	const params: AddRemoteNodeParamsRecord = {};
	for (const param of definition?.params ?? []) {
		const opt = param.optionalInfo;
		const value = opt?.value != null ? opt.value : opt?.valueOptions?.[0]?.value;
		if (value != null) {
			params[param.name] = { name: param.name, value: value as string | number | boolean };
		}
	}
	return params;
}

export const handleVideoUpscale = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput) return;

	const definition = await getDefinitionOrAwait("Phygital Creator/phygc-rnd-api-topazlabs");
	const params = buildParamsFromDefinition(definition ?? {});

	const node = await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-api-topazlabs",
		params: Object.keys(params).length ? params : undefined,
		connections: [
			{
				fromNodeId: context.socketsOutput.node.uuid,
				fromNodeSocketName: context.socketName,
				toNodeSocketName: "init_video"
			}
		],
		position: getPositionToTheRightWithPadding(context.socketsOutput.node)
	});

	setTimeout(() => {
		node?.execute();
	}, 0);
};

export const handleUpscalePro = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput) return;

	const upscaleNode = await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-magnific-api",
		params: {
			denoise: {
				name: "denoise",
				value: 0.1
			},
			tile_padding: {
				name: "tile_padding",
				value: 32
			}
		},
		connections: [
			{
				fromNodeId: context.socketsOutput.node.uuid,
				fromNodeSocketName: context.socketName,
				toNodeSocketName: "image"
			}
		],
		position: getPositionToTheRightWithPadding(context.socketsOutput.node)
	});
	upscaleNode?.execute();
};

export const handleVariation = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput) return;

	const [copiedParams] = await Editor.context.graph.serializeNodesAndThierConnections([
		context.socketsOutput.node
	]);
	copiedParams.globalId = "Phygital Creator/phygc-rnd-comfy-flux";
	copiedParams.position = getPositionToTheRightWithPadding(context.socketsOutput.node);
	copiedParams.connections.push({
		from: {
			nodeUuid: context.socketsOutput.node.uuid,
			name: context.socketName
		},
		to: {
			nodeUuid: copiedParams.uuid,
			name: "init_img"
		}
	});
	copiedParams.params.denoise = {
		name: "denoise",
		value: 0.65
	};

	const variationNode = await Editor.context.graph.pasteNodeByGlobalidAndParams(copiedParams);
	Editor.context.graph.pasteConnectionsFromTemplateParams(copiedParams);

	if (isFetchesExternalModels(variationNode.definition)) {
		variationNode.eventor.addListener(NodeEvents.MyModalsWasFetched, () => {
			variationNode.execute();
		});
		return;
	}
	setTimeout(() => {
		setTimeout(() => variationNode.execute());
	});
	return;
};

export const handleAutoMask = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput) return;

	const segmentationPrompts = ["background", "person", "face", "hair"];

	const maskNode = await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-image-segmentation",
		inputs: {
			prompt: {
				name: "prompt",
				value: pickRandomFromArray(segmentationPrompts)
			}
		},
		connections: [
			{
				fromNodeId: context.socketsOutput.node.uuid,
				fromNodeSocketName: context.socketName,
				toNodeSocketName: "img_input"
			}
		],
		position: getPositionToTheRightWithPadding(context.socketsOutput.node)
	});
	maskNode?.execute();
};

export const handleDescribe = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput) return;

	const node = await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-chat-gpt-4o-describe",
		connections: [
			{
				fromNodeId: context.socketsOutput.node.uuid,
				fromNodeSocketName: context.socketName,
				toNodeSocketName: "init_img"
			}
		],
		position: getPositionToTheRightWithPadding(context.socketsOutput.node)
	});
	node?.execute();
};

export const handleVision = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput) return;

	await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-chat-gpt-vision",
		connections: [
			{
				fromNodeId: context.socketsOutput.node.uuid,
				fromNodeSocketName: context.socketName,
				toNodeSocketName: "init_img"
			}
		],
		position: getPositionToTheRightWithPadding(context.socketsOutput.node)
	});
};

export const handleReimagine = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput) return;

	const describeNode = await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-image-captioning",
		connections: [
			{
				fromNodeId: context.socketsOutput.node.uuid,
				fromNodeSocketName: context.socketName,
				toNodeSocketName: "img_input"
			}
		],
		position: getPositionToTheRightWithPadding(context.socketsOutput.node)
	});
	if (!describeNode) return;
	const sdXlNode = await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-stable-xl",
		connections: [
			{
				fromNodeId: describeNode.uuid,
				fromNodeSocketName: "description",
				toNodeSocketName: "text_prompt"
			}
		],
		position: getPositionToTheRightWithPadding(describeNode)
	});
	sdXlNode?.execute();
};

export const handleRemoveBackground = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput) return;

	const removeBgNode = await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-ben2",
		connections: [
			{
				fromNodeId: context.socketsOutput.node.uuid,
				fromNodeSocketName: context.socketName,
				toNodeSocketName: "input_image"
			}
		],
		position: getPositionToTheRightWithPadding(context.socketsOutput.node)
	});
	removeBgNode?.execute();
};

export const handleChangeBackground = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput) return;

	await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-change-background",
		connections: [
			{
				fromNodeId: context.socketsOutput.node.uuid,
				fromNodeSocketName: context.socketName,
				toNodeSocketName: "init_img"
			}
		],
		position: getPositionToTheRightWithPadding(context.socketsOutput.node)
	});
};

export const handleZoomOut = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput || !context.node) return;
	const { node, socketsOutput } = context;
	const width = node.layout.getWidth();
	const pos = node.layout.getPosition();

	const outpaintNode = await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-comfy-flux-outpainting",
		connections: [
			{
				fromNodeId: socketsOutput.node.uuid,
				fromNodeSocketName: context.socketName,
				toNodeSocketName: "input_img"
			}
		],
		params: [
			{
				name: "outpainting_type",
				value: "right"
			}
		],
		position: { x: pos.x + width + hoverFeatureHorizontralPadding, y: pos.y }
	});
	if (!outpaintNode) return;
	onNextFrame(() => {
		outpaintNode.execute();
	});
};

export const handleRecolor = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput) return;

	const promptVariants = ["purple ", "blue ", "green ", "red ", "yellow ", "orange "];

	const [copiedParams] = await Editor.context.graph.serializeNodesAndThierConnections([
		context.socketsOutput.node
	]);

	copiedParams.globalId = "Phygital Creator/phygc-rnd-control-stable-diffusion";
	copiedParams.position = getPositionToTheRightWithPadding(context.socketsOutput.node);
	if (copiedParams.inputSocketGroup.text_prompt) {
		copiedParams.inputSocketGroup.text_prompt.value =
			pickRandomFromArray(promptVariants) + copiedParams.inputSocketGroup.text_prompt.value;
	}
	copiedParams.connections.push({
		from: {
			nodeUuid: context.socketsOutput.node.uuid,
			name: context.socketName
		},
		to: {
			nodeUuid: copiedParams.uuid,
			name: "init_img"
		}
	});
	copiedParams.params.cond_input = {
		name: "cond_input",
		value: "Edge"
	};
	copiedParams.params.steps = {
		name: "steps",
		value: 30
	};
	const recolorNode = await Editor.context.graph.pasteNodeByGlobalidAndParams(copiedParams);
	Editor.context.graph.pasteConnectionsFromTemplateParams(copiedParams);

	if (isFetchesExternalModels(recolorNode.definition)) {
		recolorNode.eventor.addListener(NodeEvents.MyModalsWasFetched, () => {
			recolorNode.execute();
		});
		return;
	}
	setTimeout(() => {
		setTimeout(() => recolorNode.execute());
	});
	recolorNode.execute();
};

// Text viewer handlers
export const handleDalleGenerate = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput) return;

	const hoverFeatureHorizontralPadding = 100;
	const width = context.socketsOutput.node.layout.getWidth();
	const pos = context.socketsOutput.node.layout.getPosition();

	const dalleNode = await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-comfy-flux",
		connections: [
			{
				fromNodeId: context.socketsOutput.node.uuid,
				fromNodeSocketName: context.socketName,
				toNodeSocketName: "text_prompt"
			}
		],
		position: { x: pos.x + width + hoverFeatureHorizontralPadding, y: pos.y }
	});
	dalleNode?.execute();
};

export const handleKlingExtend = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput || !context.viewer) return;
	const socketsOutput = context.socketsOutput;
	const findIndexOfSelf = () => {
		return socketsOutput.node
			.getSocketsOut()
			.findIndex((socket) => socket.name === context.socketName);
	};

	const node = await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-api-kling-extention",
		meta: {
			fromTaskID: socketsOutput.node.taskID,
			fromImageIndex: findIndexOfSelf()
		},
		position: getPositionToTheRightWithPadding(socketsOutput.node)
	});
	setTimeout(() => {
		node?.execute();
	}, 0);
};

export const handleSoraExtractVideoId = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput || !context.viewer) return;
	const socketsOutput = context.socketsOutput;
	const taskId = getTaskIdFromNode(socketsOutput.node);
	const findIndexOfSelf = () => {
		return socketsOutput.node
			.getSocketsOut()
			.findIndex((socket) => socket.name === context.socketName);
	};
	const node = await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-sora-extract-api",
		meta: {
			fromTaskID: taskId,
			fromImageIndex: findIndexOfSelf()
		},
		position: getPositionToTheRightWithPadding(socketsOutput.node)
	});

	setTimeout(() => {
		node?.execute();
	}, 0);
};

export const handleElevenLabsGetVoiceId = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput || !context.viewer) return;
	const socketsOutput = context.socketsOutput;
	const findIndexOfSelf = () => {
		return socketsOutput.node
			.getSocketsOut()
			.findIndex((socket) => socket.name === context.socketName);
	};

	const node = await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-elevenlabs-api-ecv",
		meta: {
			fromTaskID: socketsOutput.node.taskID,
			fromAudioIndex: findIndexOfSelf()
		},
		position: getPositionToTheRightWithPadding(socketsOutput.node)
	});
	setTimeout(() => {
		node?.execute();
	}, 0);
};

// Object3D viewer handlers
export const handleRender = async (context: HoverFeatureMenuContext) => {
	if (!context.viewer || !context.socketsOutput) return;

	// Call the existing serializeAsFile method from the viewer
	const imageFile = await (context.viewer as any).serializeAsFile();

	addLocalNodeByGlobalId({
		globalId: "Import Files",
		newOutputsFiles: [imageFile],
		position: {
			x:
				context.socketsOutput.node.layout.getPosition().x -
				context.socketsOutput.node.layout.getWidth() -
				50,
			y: context.socketsOutput.node.layout.position.y
		}
	});
};

// Additional handlers for Midjourney functionality
export const handleMidjourneyVariate = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput || !context.viewer) return;
	const socketsOutput = context.socketsOutput;
	const findIndexOfSelf = () => {
		return socketsOutput.node
			.getSocketsOut()
			.findIndex((socket) => socket.name === context.socketName);
	};

	const node = await addRemoteNodeByGlobalId({
		globalId: socketsOutput.node.definition.isNeedToMjVariate,
		meta: {
			fromTaskID: socketsOutput.node.taskID,
			fromImageIndex: findIndexOfSelf()
		},
		position: getPositionToTheRightWithPadding(socketsOutput.node)
	});
	setTimeout(() => {
		node?.execute();
	}, 0);
};

// Additional handlers for Midjourney functionality
export const handleMidjourneyGetId = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput || !context.viewer) return;
	const socketsOutput = context.socketsOutput;
	const findIndexOfSelf = () => {
		return socketsOutput.node
			.getSocketsOut()
			.findIndex((socket) => socket.name === context.socketName);
	};

	const node = await addRemoteNodeByGlobalId({
		globalId: "Phygital Creator/phygc-rnd-midjourney-api-seed",
		meta: {
			fromTaskID: socketsOutput.node.taskID,
			fromImageIndex: findIndexOfSelf()
		},
		position: getPositionToTheRightWithPadding(socketsOutput.node)
	});
	setTimeout(() => {
		node?.execute();
	}, 0);
};

export const handleMidjourneyUpscale = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput || !context.viewer) return;
	const socketsOutput = context.socketsOutput;
	const findIndexOfSelf = () => {
		return socketsOutput.node
			.getSocketsOut()
			.findIndex((socket) => socket.name === context.socketName);
	};

	const node = await addRemoteNodeByGlobalId({
		globalId: socketsOutput.node.definition.isNeedToMjUpscale,
		meta: {
			fromTaskID: socketsOutput.node.taskID,
			fromImageIndex: findIndexOfSelf()
		},
		position: getPositionToTheRightWithPadding(socketsOutput.node)
	});
	setTimeout(() => {
		node?.execute();
	}, 0);
};

export const handleMidjourneyZoomOut = async (context: HoverFeatureMenuContext) => {
	if (!context.socketsOutput || !context.viewer) return;
	const socketsOutput = context.socketsOutput;
	const findIndexOfSelf = () => {
		return socketsOutput.node
			.getSocketsOut()
			.findIndex((socket) => socket.name === context.socketName);
	};

	const node = await addRemoteNodeByGlobalId({
		globalId: socketsOutput.node.definition.isNeedToMjZoomOut,
		meta: {
			fromTaskID: socketsOutput.node.taskID,
			fromImageIndex: findIndexOfSelf()
		},
		position: getPositionToTheRightWithPadding(socketsOutput.node)
	});
	setTimeout(() => {
		node?.execute();
	}, 0);
};

export const handleMidjourneyPan = async (context: HoverFeatureMenuContext, direction: string) => {
	if (!context.socketsOutput || !context.viewer) return;
	const socketsOutput = context.socketsOutput;
	const findIndexOfSelf = () => {
		return socketsOutput.node
			.getSocketsOut()
			.findIndex((socket) => socket.name === context.socketName);
	};

	const node = await addRemoteNodeByGlobalId({
		globalId: socketsOutput.node.definition.isNeedToMjPan,
		meta: {
			fromTaskID: socketsOutput.node.taskID,
			fromImageIndex: findIndexOfSelf(),
			direction: direction
		},
		position: getPositionToTheRightWithPadding(socketsOutput.node)
	});
	setTimeout(() => {
		node?.execute();
	}, 0);
};

// Footer actions - shown as buttons in the footer
const getImageViewerFooterActions = (context: HoverFeatureMenuContext): HoverFeatureAction[] => {
	const actions: HoverFeatureAction[] = [];

	// Always add basic footer actions
	actions.push(createSaveHoverFeature(context));
	actions.push(createUpscaleHoverFeature(context));
	actions.push(createInpaintHoverFeature(context));

	return actions;
};

// Context menu actions - shown in the three dots menu
const getImageViewerContextMenuActions = (
	context: HoverFeatureMenuContext
): Menu.ClosedMenuTypesUnion[] => {
	const actions: Menu.ClosedMenuTypesUnion[] = [];

	// Fullscreen
	actions.push(
		HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
			createFullScreenHoverFeature(context)
		)
	);

	// Save
	actions.push(
		HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(createSaveHoverFeature(context))
	);

	actions.push({ type: Menu.ClosedMenuItemType.Separator });

	// Variations
	if (context.socketsOutput?.node?.definition?.isNeedToVariate) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createVariationHoverFeature(context)
			)
		);
	}

	// Edit Area (Inpaint)
	const inpaintAction = createInpaintHoverFeature(context);
	actions.push(HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(inpaintAction));

	// Upscale
	if (context.socketsOutput?.node?.definition?.isNeedToUpscale) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createUpscaleHoverFeature(context)
			)
		);
	}

	// Creative Upscale (Upscale Pro)
	if (context.socketsOutput?.node?.definition?.isNeedToDetailedUpscale) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createUpscaleProHoverFeature(context)
			)
		);
	}
	if (context.node?.definition.isNeedToZoomOut) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createExtendHoverFeature(context)
			)
		);
	}

	// Pan (only MJ Pan should be grouped)
	if (context.socketsOutput?.node?.definition?.isNeedToMjPan && context.node?.taskID) {
		const panItems: Menu.ClosedMenuTypesUnion[] = [
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createMidjourneyPanHoverFeature(context, "left")
			),
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createMidjourneyPanHoverFeature(context, "right")
			),
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createMidjourneyPanHoverFeature(context, "up")
			),
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createMidjourneyPanHoverFeature(context, "down")
			)
		];

		actions.push({
			id: "midjourney-pan",
			type: Menu.ClosedMenuItemType.Group,
			label: "Pan",
			items: panItems,
			Icon: (p) => <ArrowRight size={16} {...p} />
		});
	}

	// Zoom out
	if (context.socketsOutput?.node?.definition?.isNeedToMjZoomOut && context.node?.taskID) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createMidjourneyZoomOutHoverFeature(context)
			)
		);
	}

	if (context.socketsOutput?.node?.definition?.isNeedToMjUpscale && context.node?.taskID) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createMidjourneyUpscaleHoverFeature(context)
			)
		);
	}

	if (context.socketsOutput?.node?.definition?.isNeedToMjVariate && context.node?.taskID) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createMidjourneyVariateHoverFeature(context)
			)
		);
	}

	if (context.socketsOutput?.node?.definition?.isNeedToMjVariate && context.node?.taskID) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createMidjourneyGetIdFeature(context)
			)
		);
	}

	actions.push({ type: Menu.ClosedMenuItemType.Separator });

	actions.push(
		HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
			createChangeBackgroundHoverFeature(context)
		)
	);

	actions.push(
		HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
			createRemoveBackgroundHoverFeature(context)
		)
	);

	actions.push({ type: Menu.ClosedMenuItemType.Separator });

	actions.push(
		HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
			createVectorizeHoverFeature(context)
		)
	);

	// Crop
	if (context.socketsOutput?.node?.definition?.isNeedToCrop) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createCropHoverFeature(context)
			)
		);
	}

	// Describe
	actions.push(
		HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
			createDescribeHoverFeature(context)
		)
	);

	return actions;
};

// Footer actions - shown as buttons in the footer
const getSvgViewerFooterActions = (context: HoverFeatureMenuContext): HoverFeatureAction[] => {
	const actions: HoverFeatureAction[] = [];

	// Always add basic footer actions
	actions.push(createSaveHoverFeature(context));
	actions.push(createInpaintHoverFeature(context));

	return actions;
};

// Context menu actions - shown in the three dots menu
const getSvgViewerContextMenuActions = (
	context: HoverFeatureMenuContext
): Menu.ClosedMenuTypesUnion[] => {
	const actions: Menu.ClosedMenuTypesUnion[] = [];

	// Fullscreen
	actions.push(
		HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
			createFullScreenHoverFeature(context)
		)
	);

	// Save
	actions.push(
		HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(createSaveHoverFeature(context))
	);

	actions.push({ type: Menu.ClosedMenuItemType.Separator });

	// Variations
	if (context.socketsOutput?.node?.definition?.isNeedToVariate) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createVariationHoverFeature(context)
			)
		);
	}

	// Edit Area (Inpaint)
	const inpaintAction = createInpaintHoverFeature(context);
	actions.push(HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(inpaintAction));

	// Upscale
	if (context.socketsOutput?.node?.definition?.isNeedToUpscale) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createUpscaleHoverFeature(context)
			)
		);
	}

	// Creative Upscale (Upscale Pro)
	if (context.socketsOutput?.node?.definition?.isNeedToDetailedUpscale) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createUpscaleProHoverFeature(context)
			)
		);
	}
	if (context.node?.definition.isNeedToZoomOut) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createExtendHoverFeature(context)
			)
		);
	}

	// Pan (only MJ Pan should be grouped)
	if (context.socketsOutput?.node?.definition?.isNeedToMjPan && context.node?.taskID) {
		const panItems: Menu.ClosedMenuTypesUnion[] = [
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createMidjourneyPanHoverFeature(context, "left")
			),
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createMidjourneyPanHoverFeature(context, "right")
			),
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createMidjourneyPanHoverFeature(context, "up")
			),
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createMidjourneyPanHoverFeature(context, "down")
			)
		];

		actions.push({
			id: "midjourney-pan",
			type: Menu.ClosedMenuItemType.Group,
			label: "Pan",
			items: panItems,
			Icon: (p) => <ArrowRight size={16} {...p} />
		});
	}

	// Zoom out
	if (context.socketsOutput?.node?.definition?.isNeedToMjZoomOut && context.node?.taskID) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createMidjourneyZoomOutHoverFeature(context)
			)
		);
	}

	if (context.socketsOutput?.node?.definition?.isNeedToMjUpscale && context.node?.taskID) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createMidjourneyUpscaleHoverFeature(context)
			)
		);
	}

	if (context.socketsOutput?.node?.definition?.isNeedToMjVariate && context.node?.taskID) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createMidjourneyVariateHoverFeature(context)
			)
		);
	}

	actions.push({ type: Menu.ClosedMenuItemType.Separator });

	// actions.push(
	// 	HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
	// 		createChangeBackgroundHoverFeature(context)
	// 	)
	// );

	// actions.push(
	// 	HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
	// 		createRemoveBackgroundHoverFeature(context)
	// 	)
	// );

	// actions.push(
	// 	HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
	// 		createVectorizeHoverFeature(context)
	// 	)
	// );

	// Crop
	if (context.socketsOutput?.node?.definition?.isNeedToCrop) {
		actions.push(
			HoverFeatureMenuMethods.convertHoverFeatureActionToMenuItem(
				createCropHoverFeature(context)
			)
		);
	}

	return actions;
};

// Text Viewer Actions
const getTextViewerFooterActions = (context: HoverFeatureMenuContext): HoverFeatureAction[] => {
	const actions: HoverFeatureAction[] = [];

	const saveAction = createCopyHoverFeature(context);
	actions.push(saveAction);

	if (context.socketsOutput?.node?.definition?.isNeedDalleGenerate) {
		actions.push(createDalleGenerateHoverFeature(context));
	}

	return actions;
};

const getTextViewerContextMenuActions = (
	_context: HoverFeatureMenuContext
): Menu.ClosedMenuTypesUnion[] => {
	return []; // Text viewer has minimal context menu options for now
};

// Audio Viewer Actions
const getAudioViewerFooterActions = (context: HoverFeatureMenuContext): HoverFeatureAction[] => {
	const actions = [createSaveHoverFeature(context)];

	// Add "Get Voice ID" action only for eleven labs nodes
	if (
		context.node?.globalId === "Phygital Creator/phygc-rnd-elevenlabs-api-ttv" &&
		context.node?.meta.taskID
	) {
		actions.push(createElevenLabsGetVoiceIdHoverFeature(context));
	}

	return actions;
};

const getAudioViewerContextMenuActions = (
	_context: HoverFeatureMenuContext
): Menu.ClosedMenuTypesUnion[] => {
	return []; // Audio viewer has minimal context menu options
};

// Video Viewer Actions
const getVideoViewerFooterActions = (context: HoverFeatureMenuContext): HoverFeatureAction[] => {
	const actions = [];
	if (
		context.node?.globalId === "Phygital Creator/phygc-rnd-api-kling" &&
		(context.node?.meta?.taskID || context.node?.taskID)
	) {
		actions.push(createKlingExtendHoverFeature(context));
	}
	if (
		(context.node?.globalId?.includes?.("phygc-rnd-sora") ||
			context.node?.globalId === "Phygital Creator/phygc-rnd-sora-api") &&
		(context.node?.meta?.taskID || context.node?.taskID)
	) {
		actions.push(createSoraExtractVideoIdHoverFeature(context));
	}

	if (context.node?.definition?.isNeedToUpscaleVideo) {
		actions.push(createVideoUpscaleHoverFeature(context));
	}

	actions.push(createSaveHoverFeature(context));

	return actions;
};

const getVideoViewerContextMenuActions = (
	_context: HoverFeatureMenuContext
): Menu.ClosedMenuTypesUnion[] => {
	return []; // Video viewer has minimal context menu options
};

// Object3D Viewer Actions
const getObject3DViewerFooterActions = (context: HoverFeatureMenuContext): HoverFeatureAction[] => {
	return [createSaveHoverFeature(context), createRenderHoverFeature(context)];
};

const getObject3DViewerContextMenuActions = (
	_context: HoverFeatureMenuContext
): Menu.ClosedMenuTypesUnion[] => {
	return []; // Object3D viewer has minimal context menu options
};

// Export functions for getting footer and context menu actions
export const getFooterActionsForViewerType = (
	viewerType: ViewerType,
	context: HoverFeatureMenuContext
): HoverFeatureAction[] => {
	switch (viewerType) {
		case ViewerType.Image:
			return getImageViewerFooterActions(context);
		case ViewerType.Svg:
			return getSvgViewerFooterActions(context);
		case ViewerType.Text:
			return getTextViewerFooterActions(context);
		case ViewerType.Audio:
			return getAudioViewerFooterActions(context);
		case ViewerType.Video:
			return getVideoViewerFooterActions(context);
		case ViewerType.Object3D:
			return getObject3DViewerFooterActions(context);
		default:
			return [];
	}
};

export const getContextMenuActionsForViewerType = (
	viewerType: ViewerType,
	context: HoverFeatureMenuContext
): Menu.ClosedMenuTypesUnion[] => {
	switch (viewerType) {
		case ViewerType.Image:
			return getImageViewerContextMenuActions(context);
		case ViewerType.Svg:
			return getSvgViewerContextMenuActions(context);
		case ViewerType.Text:
			return getTextViewerContextMenuActions(context);
		case ViewerType.Audio:
			return getAudioViewerContextMenuActions(context);
		case ViewerType.Video:
			return getVideoViewerContextMenuActions(context);
		case ViewerType.Object3D:
			return getObject3DViewerContextMenuActions(context);
		default:
			return [];
	}
};

// Factory functions for creating HoverFeatureAction objects
export const createFullScreenHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "fullscreen",
	label: "Fullscreen",
	description: "Open the image in fullscreen",
	Icon: (p) => <ImagePlus size={12} {...p} />,
	IconContext: (p) => <Maximize2 size={16} {...p} />,
	handler: () => handleFullScreen(context),
	associatedGlobalIds: []
});

export const createSaveHoverFeature = (context: HoverFeatureMenuContext): HoverFeatureAction => {
	let description = context.assetName
		? `Format ${context.assetName?.split(".").pop()?.toUpperCase()}`
		: "Save the file";
	if (!getIsSaveFunctionalityAvailable(getUserStore())) {
		description = "Available for Starter plan users and above";
	}
	return {
		id: "save",
		label: "Save",
		description: description,
		Icon: (p) => <Download size={12} {...p} />,
		IconContext: (p) => <Download size={16} {...p} />,
		handler: () => handleSave(context),
		disabled: () => !getIsSaveFunctionalityAvailable(getUserStore()),
		associatedGlobalIds: []
	};
};

export const createCopyHoverFeature = (context: HoverFeatureMenuContext): HoverFeatureAction => ({
	id: "copy",
	label: "Copy",
	description: "Copy the text to clipboard",
	Icon: (p) => <CopyIcon size={12} {...p} />,
	IconContext: (p) => <CopyIcon size={16} {...p} />,
	handler: () => handleCopy(context),
	associatedGlobalIds: []
});

export const createInpaintHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "edit_area",
	label: "Edit area",
	description: "Select part of the image to change",
	Icon: Icons.EditAreaIcon.default,
	IconContext: EditAreaIcon16,
	handler: () => handleInpaint(context),
	associatedGlobalIds: []
});

export const createCropHoverFeature = (context: HoverFeatureMenuContext): HoverFeatureAction => ({
	id: "crop",
	label: "Crop",
	description: "Cut part of the image",
	Icon: (p) => <Crop size={12} {...p} />,
	IconContext: (p) => <Crop size={16} {...p} />,
	handler: () => handleCrop(context),
	associatedGlobalIds: ["ImageCropper"]
});

export const createUpscaleHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "upscale",
	label: "Upscale",
	description: "Make the image bigger and better",
	Icon: Icons.ScaleIcon.default,
	IconContext: () => <Scaling size={16} />,
	handler: () => handleUpscale(context),
	associatedGlobalIds: ["Phygital Creator/phygc-rnd-upscale"]
});

export const createVectorizeHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "vectorize",
	label: "Vectorize",
	description: "Convert the image to vector format",
	Icon: () => <SplinePointer size={12} />,
	IconContext: () => <SplinePointer size={16} />,
	handler: () => handleVectorize(context),
	associatedGlobalIds: ["Phygital Creator/phygc-rnd-api-recraft-vectorize"]
});

export const createUpscaleProHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "upscale-pro",
	label: "Creative Upscale",
	description: "Make the image bigger with new details",
	Icon: Icons.CreativeScale16.default,
	IconContext: Icons.CreativeScale16.default,
	handler: () => handleUpscalePro(context),
	associatedGlobalIds: ["Phygital Creator/phygc-rnd-magnific-api"]
});

export const createVariationHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "variation",
	label: "Variations",
	description: "Make new versions of the same image",
	Icon: (p) => <Shuffle size={12} {...p} />,
	IconContext: (p) => <Shuffle size={16} {...p} />,
	handler: () => handleVariation(context),
	associatedGlobalIds: ["Phygital Creator/phygc-rnd-comfy-flux"]
});

export const createAutoMaskHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "auto-mask",
	label: "Auto Mask",
	description: "Automatically mask the image",
	Icon: (p) => <ImagePlus size={12} {...p} />,
	IconContext: (p) => <ImagePlus size={16} {...p} />,
	handler: () => handleAutoMask(context),
	associatedGlobalIds: ["Phygital Creator/phygc-rnd-image-segmentation"]
});

export const createDescribeHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "describe",
	label: "Describe",
	description: "Get a text description of the image",
	Icon: (p) => <ImagePlus size={12} {...p} />,
	IconContext: (p) => <ListCollapse size={16} {...p} />,
	handler: () => handleDescribe(context),
	associatedGlobalIds: ["Phygital Creator/phygc-rnd-chat-gpt-4o-describe"]
});

export const createVisionHoverFeature = (context: HoverFeatureMenuContext): HoverFeatureAction => ({
	id: "vision",
	label: "Vision",
	description: "Get a text description of the image",
	Icon: (p) => <ImagePlus size={12} {...p} />,
	IconContext: VisionIcon16,
	handler: () => handleVision(context),
	associatedGlobalIds: ["Phygital Creator/phygc-rnd-chat-gpt-vision"]
});

export const createReimagineHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "reimagine",
	label: "Reimagine",
	Icon: (p) => <ImagePlus size={12} {...p} />,
	IconContext: () => <RefreshCcw size={16} />,
	handler: () => handleReimagine(context),
	associatedGlobalIds: [
		"Phygital Creator/phygc-rnd-image-captioning",
		"Phygital Creator/phygc-rnd-stable-xl"
	]
});

export const createRecolorHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "recolor",
	label: "Recolor",
	Icon: RecolorIcon,
	IconContext: RecolorIcon16,
	handler: () => handleRecolor(context),
	associatedGlobalIds: ["Phygital Creator/phygc-rnd-control-stable-diffusion"]
});

export const createExtendHoverFeature = (context: HoverFeatureMenuContext): HoverFeatureAction => ({
	id: "extend",
	label: "Extend",
	Icon: (p) => <ImagePlus size={12} {...p} />,
	IconContext: () => <ImageUpscale size={16} />,
	description: "Add more space around the image",
	handler: () => handleZoomOut(context),
	associatedGlobalIds: ["ImageOutPaint", "Phygital Creator/phygc-rnd-comfy-flux-outpainting"]
});

export const createChangeBackgroundHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "change-background",
	label: "Change Background",
	Icon: (p) => <ImagePlus size={12} {...p} />,
	IconContext: ChangeBackgroundIcon16,
	description: "Generate a new background",
	handler: () => handleChangeBackground(context),
	associatedGlobalIds: ["Phygital Creator/phygc-rnd-change-background"]
});

export const createRemoveBackgroundHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "remove-background",
	label: "Remove Background",
	Icon: (p) => <ImagePlus size={12} {...p} />,
	IconContext: Icons.RemoveBackground16.deafult,
	description: "Delete the background",
	handler: () => handleRemoveBackground(context),
	associatedGlobalIds: ["Phygital Creator/phygc-rnd-ben2"]
});

export const createDalleGenerateHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "dalle-generate",
	label: "Create image",
	description: "Generate an image",
	Icon: (p) => <ImagePlus size={12} {...p} />,
	IconContext: (p) => <ImagePlus size={16} {...p} />,
	isPremium: () => true,
	handler: () => handleDalleGenerate(context),
	associatedGlobalIds: ["Phygital Creator/phygc-rnd-comfy-flux"]
});

export const createRenderHoverFeature = (context: HoverFeatureMenuContext): HoverFeatureAction => ({
	id: "render",
	label: "Render",
	description: "Render the view",
	Icon: (p) => <ImagePlus size={12} {...p} />,
	IconContext: RenderIcon16,
	handler: () => handleRender(context),
	associatedGlobalIds: ["Import Files"]
});

export const createKlingExtendHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "kling-extend",
	label: "Extend",
	description: "Extend the video",
	Icon: (p) => <ImagePlus size={12} {...p} />,
	IconContext: () => <ImageUpscale size={16} />,
	handler: () => handleKlingExtend(context),
	associatedGlobalIds: ["Phygital Creator/phygc-rnd-api-kling-extention"]
});

export const createSoraExtractVideoIdHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "sora-extract-video-id",
	label: "Extract Video ID",
	description: "Extract video ID for Sora Remix",
	Icon: (p) => <ImagePlus size={12} {...p} />,
	IconContext: VisionIcon16,
	handler: () => handleSoraExtractVideoId(context),
	associatedGlobalIds: ["Phygital Creator/phygc-rnd-sora-extract-api"]
});

export const createElevenLabsGetVoiceIdHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "elevenlabs-get-voice-id",
	label: "Get Voice ID",
	description: "Get voice characteristics",
	Icon: (p) => <ImagePlus size={12} {...p} />,
	IconContext: VisionIcon16,
	handler: () => handleElevenLabsGetVoiceId(context),
	associatedGlobalIds: ["Phygital Creator/phygc-rnd-elevenlabs-api-ecv"]
});

export const createMidjourneyVariateHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "midjourney-variate",
	label: "Mj Variate",
	description: "Create a variation of the image",
	Icon: (p) => <Shuffle size={12} {...p} />,
	IconContext: (p) => <Shuffle size={16} {...p} />,
	handler: () => handleMidjourneyVariate(context),
	associatedGlobalIds: [context.node?.definition?.isNeedToMjVariate || ""]
});

export const createMidjourneyGetIdFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "midjourney-get-seed",
	label: "Mj Get Seed",
	description: "Get the seed of the image",
	Icon: (p) => <ImagePlus size={12} {...p} />,
	IconContext: VisionIcon16,
	handler: () => handleMidjourneyGetId(context),
	associatedGlobalIds: ["Phygital Creator/phygc-rnd-midjourney-api-seed"]
});

export const createMidjourneyUpscaleHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "midjourney-upscale",
	label: "Mj Upscale",
	description: "Upscale the image",
	Icon: Icons.ScaleIcon.default,
	IconContext: () => <Scaling size={16} />,
	handler: () => handleMidjourneyUpscale(context),
	associatedGlobalIds: [context.node?.definition.isNeedToMjUpscale || ""] // Dynamic globalId from context.socketsOutput.node.definition.isNeedToMjUpscale
});

export const createMidjourneyZoomOutHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "midjourney-zoomout",
	label: "Mj Zoom out",
	description: "Show more around the image",
	Icon: (p) => <ImagePlus size={12} {...p} />,
	IconContext: ZoomOutIcon16,
	handler: () => handleMidjourneyZoomOut(context),
	associatedGlobalIds: [context.node?.definition.isNeedToMjZoomOut || ""] // Dynamic globalId from context.socketsOutput.node.definition.isNeedToMjZoomOut
});

export const createVideoUpscaleHoverFeature = (
	context: HoverFeatureMenuContext
): HoverFeatureAction => ({
	id: "upscale",
	label: "Upscale",
	description: "Make the video bigger and sharper",
	Icon: Icons.ScaleIcon.default,
	IconContext: () => <Scaling size={16} />,
	handler: () => handleVideoUpscale(context),
	associatedGlobalIds: ["Phygital Creator/phygc-rnd-api-topazlabs"]
});

export const createMidjourneyPanHoverFeature = (
	context: HoverFeatureMenuContext,
	direction: string
): HoverFeatureAction => {
	const getDescription = (dir: string) => {
		switch (dir) {
			case "left":
				return "Move the image to the left";
			case "right":
				return "Move the image to the right";
			case "up":
				return "Move the image up";
			case "down":
				return "Move the image down";
			default:
				return "Move the image";
		}
	};

	const getRotation = (dir: string) => {
		switch (dir) {
			case "left":
				return "rotate-180";
			case "right":
				return "";
			case "up":
				return "-rotate-90";
			case "down":
				return "rotate-90";
			default:
				return "rotate(0deg)";
		}
	};

	return {
		id: `midjourney-pan-${direction}`,
		label: `Pan ${direction}`,
		description: getDescription(direction),
		Icon: (p) => <ImagePlus size={12} {...p} />,
		IconContext: (props) => <ArrowRight size={16} class={getRotation(direction)} {...props} />,
		handler: () => handleMidjourneyPan(context, direction),
		associatedGlobalIds: [] // Dynamic globalId from context.socketsOutput.node.definition.isNeedToMjPan
	};
};
